/**
 * @name setScrollTrigger
 */
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

/**
 * @name setScrollTrigger
 * @description 各ページのアニメーションを呼び出す
 */
export function setScrollTrigger() {
    let getPathName = location.pathname;
    window.document.body.classList.contains("home") && setScrollTrigger__top();
    getPathName.match(/(about)/g) && setScrollTrigger__page('about');
};

/**
 * 初期値
 */
const html = document.documentElement;
let mm = gsap.matchMedia();
let props = {
    action: { duration: 1, ease: 'Quart.easeOut', overwrite: true, },
    trigger: { start: `top 65%`, end: `top 65%`, once: true },
    scrub: {
        start: `top bottom`,
        end: 'bottom top',
        scrub: 1.5,
        invalidateOnRefresh: true,
    }
}

/**
 * @name setScrollTriggerInit
 * @description 共通アニメーション
 * @param {string} getTrigger - トリガー要素
 * @param {string} type - アニメーションタイプ
 */
function setScrollTriggerInit(getTrigger, type = '') {

    // フェードイン
    const fadeIn = () => {
        let fadeIn__item = getTrigger.querySelectorAll('.--js_fadeIn__item');
        if (fadeIn__item.length > 0) {
            gsap.set(fadeIn__item, { opacity: 0, });
            ScrollTrigger.batch(getTrigger, {
                onEnter: () => gsap.to([fadeIn__item], { ...props.action, opacity: 1, stagger: 0.1, }),
                ...props.trigger,
                // markers: true,
            });
        }
    }

    // フェードアップ
    const fadeUp = () => {
        let fadeUp__item = getTrigger.querySelectorAll('.--js_fadeUp__item');
        if (fadeUp__item.length > 0) {
            gsap.set(fadeUp__item, { opacity: 0, y: "25%" });
            ScrollTrigger.batch(getTrigger, {
                onEnter: () => gsap.to([fadeUp__item], { ...props.action, opacity: 1, y: 0, stagger: 0.1, }),
                ...props.trigger,
                // markers: true,
            });
        }
    }

    // フェードダウン
    const fadeDown = () => {
        let fadeDown__item = getTrigger.querySelectorAll('.--js_fadeDown__item');
        if (fadeDown__item.length > 0) {
            gsap.set(fadeDown__item, { opacity: 0, y: "-50%" });
            ScrollTrigger.batch(getTrigger, {
                onEnter: () => gsap.to([fadeDown__item], { ...props.action, opacity: 1, y: 0, stagger: 0.2, }),
                ...props.trigger,
                // markers: true,
            });
        }
    }

    //スケールダウン
    const scaleDown = () => {
        let scaleDown__item = getTrigger.querySelectorAll('.--js_scaleDown__item');
        if (scaleDown__item.length > 0) {
            gsap.set(scaleDown__item, { scale: 1.1, opacity: 0, });
            ScrollTrigger.batch(getTrigger, {
                onEnter: () => gsap.to([scaleDown__item], { ...props.action, scale: 1, opacity: 1, stagger: 0.1, }),
                ...props.trigger,
                // markers: true,
            });
        }
    }

    // パララックス - 背景
    const parallaxElBg = () => {
        let rect = getTrigger.getBoundingClientRect();
        let triggerHeight = rect.height;
        let el = getTrigger.querySelectorAll('.--js_parallax__item__bg');
        if (el.length > 0) {
            let elHeight = el[0].clientHeight;
            mm.add("(min-width: 835px)", () => {
                gsap.to(el, {
                    y: `${-(elHeight - triggerHeight)}px`, ease: "none", scrollTrigger: { trigger: getTrigger, ...props.scrub }
                })
            })
        }
    }

    // パララックス - 要素を上に移動
    const parallaxElTop = () => {
        let rect = getTrigger.getBoundingClientRect();
        let triggerHeight = rect.height;
        let el = getTrigger.querySelectorAll('.--js_parallax__item__top');
        if (el.length > 0) {
            mm.add("(min-width: 835px)", () => {
                gsap.set(el, { y: `0` })
                gsap.to(el, { y: `${triggerHeight / 4}px`, ease: "none", scrollTrigger: { trigger: getTrigger, ...props.scrub } })
            })
        }
    }

    // パララックス - 要素を下に移動
    const parallaxElBottom = () => {
        let rect = getTrigger.getBoundingClientRect();
        let triggerHeight = rect.height;
        let el = getTrigger.querySelectorAll('.--js_parallax__item__bottom');
        if (el.length > 0) {
            mm.add("(min-width: 835px)", () => {
                gsap.set(el, { y: `0` })
                gsap.to(el, {
                    y: `-${triggerHeight / 4}px`, ease: "none", scrollTrigger: { trigger: getTrigger, ...props.scrub }
                })
            })
        }
    }

    // SVG path - SVGのパスをスクロールに合わせて描画
    const drawerPath = () => {
        let el = getTrigger.querySelectorAll('.--js_drawer__item');
        if (el.length > 0) {
            gsap.to(el, {
                strokeDashoffset: 0,
                ease: "none",
                scrollTrigger: {
                    trigger: getTrigger,
                    start: `top 50%`,
                    end: '90% 50%',
                    scrub: 1.5,
                    invalidateOnRefresh: true,
                },
            })
        }
    }

    // グラデーション - 背景色を変更 - トリガー要素が画面内に入ったらグラデーションを変更
    const gradient = () => {
        let dataTrigger = getTrigger.dataset.trigger;

        let toggleProps = {
            add: () => html.classList.add('--isChangeGradient'),
            remove: () => html.classList.remove('--isChangeGradient'),
        }

        let triggerPos = {
            start: 'top 65%',
            end: 'top 65%',
        }

        mm.add("(min-width: 835px)", () => {
            if (dataTrigger === 'GradientEnter') {
                ScrollTrigger.batch(getTrigger, {
                    onEnter: () => toggleProps.add(),
                    onLeaveBack: () => toggleProps.remove(),
                    ...triggerPos,
                })
            }

            if (dataTrigger === 'GradientLeave') {
                ScrollTrigger.batch(getTrigger, {
                    onEnter: () => toggleProps.remove(),
                    onLeaveBack: () => toggleProps.add(),
                    ...triggerPos,
                })
            }
        })
    }

    // アニメーションのオプション
    type == 'fadeIn' && fadeIn();
    type == 'fadeUp' && fadeUp();
    type == 'fadeDown' && fadeDown();
    type == 'scaleDown' && scaleDown();
    type == 'parallaxElBg' && parallaxElBg();
    type == 'parallaxElTop' && parallaxElTop();
    type == 'parallaxElBottom' && parallaxElBottom();
    type == 'drawerPath' && drawerPath();
    type == 'gradient' && gradient();
}

/**
 * @name setScrollTrigger__top
 * @description トップページ用アニメーション
 */
function setScrollTrigger__top() {
    let setTrigger = document.querySelectorAll('.--js_setTrigger');
    setTrigger.forEach((getTrigger) => {
        setScrollTriggerInit(getTrigger, 'fadeIn');
        setScrollTriggerInit(getTrigger, 'fadeUp');
        setScrollTriggerInit(getTrigger, 'fadeDown');
        setScrollTriggerInit(getTrigger, 'parallaxElBg');
        setScrollTriggerInit(getTrigger, 'parallaxElTop');
        setScrollTriggerInit(getTrigger, 'parallaxElBottom');
        setScrollTriggerInit(getTrigger, 'drawerPath');
        setScrollTriggerInit(getTrigger, 'gradient');
    });
}

/**
 * @name setScrollTrigger__page
 * @description 下層ページ用アニメーション
 */
function setScrollTrigger__page(path) {
    let setTrigger = document.querySelectorAll('.--js_setTrigger');
    const page__about = () => setTrigger.forEach((getTrigger) => {
        setScrollTriggerInit(getTrigger, 'fadeIn');
        setScrollTriggerInit(getTrigger, 'fadeUp');
        setScrollTriggerInit(getTrigger, 'gradient')
    });

    path == 'about' && page__about();
}